import type { PoiKind } from "@rietz/shared";

/**
 * Colours for named points of interest — the ONE place a POI kind becomes a
 * colour, read by both the world labels (`game/poiLabels.ts`) and the town map
 * (`ui/townMap.ts`), which must agree. Brighter than the alpine scenery,
 * grouped by what the player is looking for: hospitality warm, retail green,
 * civic blue, sacred off-white, emergency red, school cyan.
 */

export const POI_COLORS: Record<PoiKind, number> = {
  // Hospitality — where a raid's loot and a player's landmarks both cluster.
  restaurant: 0xe8a94b,
  cafe: 0xdcae74,
  hotel: 0xf2c86e,
  // Retail.
  shop: 0x82c98d,
  // Administration and work.
  civic: 0x79aeea,
  office: 0x93a9c6,
  // Sacred and monumental: the things you navigate by from across the valley.
  church: 0xe9e3d4,
  landmark: 0xf1ecdd,
  // Emergency services.
  fire_station: 0xe2604e,
  // Education.
  school: 0x6fd0d8,
};

/** Fallback for the (validated, but data-driven) kind field. */
const POI_FALLBACK = 0xd8dee6;

export function poiColor(kind: PoiKind): number {
  return POI_COLORS[kind] ?? POI_FALLBACK;
}

/** `0xRRGGBB` as a CSS colour string, for 2D canvas contexts. */
export function cssHex(rgb: number): string {
  return `#${(rgb & 0xffffff).toString(16).padStart(6, "0")}`;
}

/**
 * `0xRRGGBB` scaled per channel and returned as CSS — the town map shades a
 * solid by height in RGB, keeping the ground plate flat.
 */
export function cssHexScaled(rgb: number, factor: number): string {
  const r = clampByte(((rgb >> 16) & 0xff) * factor);
  const g = clampByte(((rgb >> 8) & 0xff) * factor);
  const b = clampByte((rgb & 0xff) * factor);
  return `rgb(${r},${g},${b})`;
}

function clampByte(v: number): number {
  if (!Number.isFinite(v)) return 0;
  const n = Math.round(v);
  return n < 0 ? 0 : n > 255 ? 255 : n;
}
